import { useMemo, useCallback } from 'react'
import { useQuery } from '@pylot-data/hooks/use-query'
import { COUNTRY_QUERY } from './graphql/countryQuery'
import type { RequestInit } from '@vercel/fetch'
import type { SWRConfiguration } from 'swr'
import type { Country } from '@pylot-data/pylotschema'

export type OutputInterface = {
    countries: Country[]
}

export const useCountries = (
    swrOptions?: SWRConfiguration,
    fetchOptions?: RequestInit
) => {
    const swrResponse = useQuery<null, OutputInterface>(
        COUNTRY_QUERY,
        null,
        swrOptions,
        fetchOptions
    )
    const isLoading = !swrResponse?.data && !swrResponse?.error

    const countries = useMemo(() => {
        const countriesData = swrResponse?.data?.data?.countries || []
        return countriesData.sort((a, b) => {
            return String(a?.full_name_locale)?.localeCompare(
                String(b?.full_name_locale)
            )
        })
    }, [swrResponse?.data?.data?.countries])

    const countryNameLocale = useMemo(() => {
        const countryMap = new Map()

        if (countries) {
            countries.forEach((country) => {
                countryMap.set(
                    country.two_letter_abbreviation,
                    country.full_name_locale
                )
            })
        }

        return countryMap
    }, [countries])

    const getCountryByCode = useCallback(
        (code: string): Country[] | [] => {
            if (!countries) return []
            return countries.filter(
                (country) => country?.two_letter_abbreviation === code
            )
        },
        [countries]
    )

    return {
        isLoading,
        countries,
        countryNameLocale,
        getCountryByCode,
        ...swrResponse
    }
}
